import {
  ArrowPathIcon,
  DocumentMagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { ArtifactUnpacked } from '@/services/types/artifact-types';

interface ArtifactBulkActionsProps {
  selectedArtifacts: ArtifactUnpacked[];
  selectedCount: number;
  onClearSelection: () => void;
  matterId: string;
}

export const ArtifactBulkActions = ({
  selectedArtifacts,
  selectedCount,
  onClearSelection,
  matterId,
}: ArtifactBulkActionsProps) => {
  const [bulkRegenerateDialogOpen, setBulkRegenerateDialogOpen] =
    useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isExtractingCoverage, setIsExtractingCoverage] = useState(false);

  const handleBulkRegenerateDigests = async () => {
    if (selectedCount === 0) return;

    setIsRegenerating(true);
    try {
      // TODO: Implement bulk digest regeneration API call
      // await regenerateDigests({ artifactIds: selectedArtifacts.map(a => a.id) });

      toast.success(
        `Digest regeneration started for ${selectedCount} artifact${selectedCount !== 1 ? 's' : ''}`,
      );
      setBulkRegenerateDialogOpen(false);
      onClearSelection();
    } catch (error) {
      console.error('Failed to regenerate digests:', error);
      toast.error('Failed to start digest regeneration');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleBulkExtractCoverage = async () => {
    if (selectedCount === 0) return;

    setIsExtractingCoverage(true);
    try {
      // Get artifact IDs from selected artifacts
      const artifactIds = selectedArtifacts.map((artifact) => artifact.id);

      // TODO: Implement bulk coverage extraction API call
      // await regenerateCoverage({ matterId, artifactIds });

      toast.success(
        `Coverage extraction started for ${selectedCount} artifact${selectedCount !== 1 ? 's' : ''}`,
      );
      onClearSelection();
    } catch (error) {
      console.error('Failed to extract coverage:', error);
      toast.error('Failed to start coverage extraction');
    } finally {
      setIsExtractingCoverage(false);
    }
  };

  if (selectedCount === 0) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-background border rounded-lg shadow-lg p-4 z-50">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">
            {selectedCount} artifact{selectedCount !== 1 ? 's' : ''} selected
          </span>

          <div className="flex gap-2">
            <Button
              onClick={handleBulkExtractCoverage}
              disabled={isExtractingCoverage}
              outline
            >
              <DocumentMagnifyingGlassIcon className="h-4 w-4 mr-2" />
              {isExtractingCoverage ? 'Extracting...' : 'Extract Coverage'}
            </Button>

            <Button
              onClick={() => setBulkRegenerateDialogOpen(true)}
              disabled={isRegenerating}
              outline
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Regenerate Digests
            </Button>

            <Button onClick={onClearSelection} plain>
              Cancel
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk regenerate confirmation dialog */}
      <Dialog
        open={bulkRegenerateDialogOpen}
        onClose={setBulkRegenerateDialogOpen}
      >
        <DialogTitle>Regenerate Artifact Digests</DialogTitle>
        <DialogBody>
          <div className="space-y-4">
            <p>
              Are you sure you want to regenerate digests for {selectedCount}{' '}
              artifact{selectedCount !== 1 ? 's' : ''}? This will reprocess the
              artifacts and may take some time.
            </p>

            <div className="flex justify-end gap-2">
              <Button onClick={() => setBulkRegenerateDialogOpen(false)} plain>
                Cancel
              </Button>
              <Button
                onClick={handleBulkRegenerateDigests}
                disabled={isRegenerating}
              >
                {isRegenerating ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  'Regenerate Digests'
                )}
              </Button>
            </div>
          </div>
        </DialogBody>
      </Dialog>
    </>
  );
};
